/**
 * NotificationsSDKWrapper - SDK Wrapper for notification operations
 *
 * Backward-compatible interface for NotificationsApiService using the generated
 * tasks-and-notifications SDK. Maps the two legacy methods to SDK endpoints.
 *
 * Migration note — org_id handling:
 *   The legacy AbstractApiService put org_id in THREE places: the query string
 *   (GET, via addOrgIdQueryString), the request body (PATCH, via payload.org_id),
 *   AND the X-ORG-ID header. The SDK sends org_id ONLY as the X-ORG-ID header
 *   (injected by the custom-axios-instance mutator). The header is the
 *   authoritative path (doc2-api resolves the org from X-ORG-ID), so dropping the
 *   redundant query/body org_id is the intended behavior change. The wrapper does
 *   NOT re-add org_id — the mutator owns it.
 *
 * Pilot: first AbstractApiService → SDK migration (template for the rest).
 */

import { BaseSDKWrapper } from './BaseSDKWrapper';
import { getTasksAndNotifications } from '../tasks-and-notifications/tasks-and-notifications';
import type { dynamicObj } from 'src/pages/assets';

export class NotificationsSDKWrapper extends BaseSDKWrapper {
  private notificationsSDK: ReturnType<typeof getTasksAndNotifications>;

  constructor() {
    super('NotificationsSDK');
    this.notificationsSDK = getTasksAndNotifications();
  }

  /**
   * List notifications. `params` is forwarded verbatim as query parameters —
   * axios serializes them, replacing the legacy manual encodeQueryData().
   */
  async getNotifications(params: dynamicObj): Promise<any> {
    this.logOperation('getNotifications', params);
    return this.execute(
      () =>
        this.notificationsSDK.notificationsListTasksAndNotificationsNotificationsGet(
          params as any,
        ),
      'getNotifications',
    );
  }

  /**
   * Mark a notification as read. Body carries only `{ seen: true }`; org_id
   * travels via the X-ORG-ID header (mutator), not the body.
   */
  async markNotificationAsRead(id: string): Promise<any> {
    this.logOperation('markNotificationAsRead', { id });
    return this.execute(
      () =>
        this.notificationsSDK.patchNotificationTasksAndNotificationsNotificationsNotificationIdPatch(
          id,
          { seen: true } as any,
        ),
      'markNotificationAsRead',
    );
  }
}
